import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Form, FormButton, FormUploadButton } from '../../interfaces/form.interface';

export interface ReactiveFormImageChange {
    fieldName: string;
    imageName: string;
}

@Component({
    selector: 'app-reactive-form-header',
    standalone: false,
    templateUrl: './form-header.component.html',
    styleUrls: ['./form-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactiveFormHeaderComponent<T> {
    @Input({ required: true }) public form!: Form<T>;
    @Input({ required: true }) public model!: FormGroup;
    @Input() public isLoading = false;
    @Input() public headerButtons: FormButton[] = [];

    @Output() public headerButtonClick = new EventEmitter<FormButton>();
    @Output() public imageChange = new EventEmitter<ReactiveFormImageChange>();

    /**
     * Returns every enabled upload action configured for the form header.
     *
     * The legacy `uploadButton` is still supported, while `uploadButtons` allows
     * forms such as machinery to show more than one file upload action inside the
     * reactive form header.
     *
     * @public
     *
     * @returns Enabled upload actions.
     */
    public getUploadButtons(): FormUploadButton[] {
        const legacyUploadButton = this.form.uploadButton.isEnabled ? [this.form.uploadButton] : [];
        const uploadButtons = this.form.uploadButtons ?? [];

        return legacyUploadButton.concat(uploadButtons).filter((button) => button.isEnabled);
    }// getUploadButtons();

    /**
     * Emits the uploaded file name using the configured form field.
     *
     * @public
     *
     * @param imageName - File name returned by the upload component.
     * @param uploadButton - Upload action configuration that owns the field.
     *
     * @returns Nothing.
     */
    public onImageNameChange(imageName: string, uploadButton: FormUploadButton): void {
        this.imageChange.emit({
            fieldName: this.getUploadFieldName(uploadButton),
            imageName,
        });
    }// onImageNameChange();

    /**
     * Provides a stable track value for upload buttons.
     *
     * @public
     *
     * @param index - Upload button index.
     * @param uploadButton - Upload action configuration.
     *
     * @returns Stable track value.
     */
    public trackUploadButton(index: number, uploadButton: FormUploadButton): string {
        return `${this.getUploadFieldName(uploadButton)}-${index}`;
    }// trackUploadButton();

    public getUploadFieldName(uploadButton: FormUploadButton): string {
        return uploadButton.imageName ?? 'imagen';
    }// getUploadFieldName();

    public getUploadFolder(uploadButton: FormUploadButton): string {
        return uploadButton.imageFolder ?? this.form.name;
    }// getUploadFolder();

    public getUploadDisplay(uploadButton: FormUploadButton): 'inline' | 'link' | 'header' {
        return uploadButton.imageDisplay ?? 'inline';
    }// getUploadDisplay();

    public getUploadAccept(uploadButton: FormUploadButton): string {
        return uploadButton.accept ?? 'image/*';
    }// getUploadAccept();

    /**
     * Returns the identifier that must be posted with legacy upload endpoints.
     *
     * @public
     *
     * @param uploadButton - Upload action configuration.
     *
     * @returns The current form identifier or null when it is not available yet.
     */
    public getUploadId(uploadButton: FormUploadButton): string | number | null {
        const idField = uploadButton.idField ?? 'id';
        const controlValue = this.model.get(idField)?.value as string | number | null | undefined;
        const rawValue = this.model.getRawValue() as Record<string, unknown>;
        const value = controlValue ?? rawValue[idField];

        if (value === null || value === undefined) {
            return null;
        }// if();

        if (typeof value === 'number') {
            return value;
        }// if();

        const textValue = String(value).trim();

        return textValue.length > 0 ? textValue : null;
    }// getUploadId();

}
